import { Controller } from "@hotwired/stimulus"

// Combobox multi-select minimal, sans dépendance.
// - Source de vérité : un <select multiple> natif (caché visuellement).
// - UI : input de recherche + chips pour les sélections + dropdown filtrable.
// - Form submission : inchangé, Rails lit les <option selected>.
//
// Usage côté ERB :
//   <div data-controller="multi-select" data-multi-select-placeholder-value="...">
//     <%= form.select :topic_ids, options_from_collection_for_select(...),
//           {}, multiple: true, data: { multi_select_target: "select" } %>
//   </div>
export default class extends Controller {
  static targets = ["select"]
  static values  = { placeholder: { type: String, default: "Choisir..." } }

  connect() {
    this.buildUI()
    this.renderChips()
    this.boundOnDocumentClick = this.onDocumentClick.bind(this)
    document.addEventListener("click", this.boundOnDocumentClick)
  }

  disconnect() {
    document.removeEventListener("click", this.boundOnDocumentClick)
  }

  buildUI() {
    // Cache le select natif sans casser l'accessibilité du form submit.
    this.selectTarget.style.display = "none"
    this.selectTarget.setAttribute("aria-hidden", "true")
    this.selectTarget.setAttribute("tabindex", "-1")

    const wrapper = document.createElement("div")
    wrapper.className = "relative"

    const box = document.createElement("div")
    box.className = "min-h-[42px] flex flex-wrap items-center gap-1 border border-gray-300 rounded-md bg-white px-2 py-1 cursor-text focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500"
    box.addEventListener("click", () => this.searchEl.focus())

    const search = document.createElement("input")
    search.type = "text"
    search.placeholder = this.placeholderValue
    search.className = "flex-grow min-w-[120px] text-sm text-gray-900 placeholder-gray-400 px-1 py-1"
    search.style.cssText = "border:none;background:transparent;outline:none;box-shadow:none;"
    search.addEventListener("input", () => this.filter())
    search.addEventListener("focus", () => this.openDropdown())
    search.addEventListener("keydown", (e) => this.onKeydown(e))

    box.appendChild(search)

    const dropdown = document.createElement("div")
    dropdown.className = "hidden absolute z-50 mt-1 left-0 right-0 max-h-60 overflow-auto border border-gray-200 rounded-md bg-white shadow-lg text-sm text-gray-900"

    wrapper.appendChild(box)
    wrapper.appendChild(dropdown)
    this.element.appendChild(wrapper)

    // Refs locales (pas Stimulus targets, pour éviter le timing async).
    this.boxEl = box
    this.searchEl = search
    this.dropdownEl = dropdown

    this.renderDropdown()
  }

  // -- Render helpers --

  renderChips() {
    // On retire tous les chips existants (tous les enfants sauf l'input).
    Array.from(this.boxEl.querySelectorAll("[data-chip]")).forEach((c) => c.remove())

    this.selectedOptions().forEach((opt) => {
      const chip = document.createElement("span")
      chip.dataset.chip = "true"
      chip.className = "inline-flex items-center gap-1 bg-gray-100 text-gray-800 text-xs font-medium px-2 py-1 rounded"
      chip.dataset.value = opt.value

      const label = document.createElement("span")
      label.textContent = opt.text
      chip.appendChild(label)

      const remove = document.createElement("button")
      remove.type = "button"
      remove.className = "text-gray-500 hover:text-gray-900 leading-none"
      remove.innerHTML = "&times;"
      remove.setAttribute("aria-label", `Retirer ${opt.text}`)
      remove.addEventListener("click", (e) => {
        e.stopPropagation()
        this.toggle(opt.value, false)
      })
      chip.appendChild(remove)

      // Insère avant l'input de recherche pour que la recherche reste à droite.
      this.boxEl.insertBefore(chip, this.searchEl)
    })
  }

  renderDropdown(filter = "") {
    this.dropdownEl.innerHTML = ""
    const q = filter.trim().toLowerCase()
    const unselected = this.allOptions().filter((o) => !o.selected)
    const filtered = q ? unselected.filter((o) => o.text.toLowerCase().includes(q)) : unselected

    if (filtered.length === 0) {
      const empty = document.createElement("div")
      empty.className = "px-3 py-2 text-gray-500 italic"
      empty.textContent = q ? "Aucun résultat" : "Tout est déjà sélectionné"
      this.dropdownEl.appendChild(empty)
      return
    }

    filtered.forEach((opt) => {
      const item = document.createElement("button")
      item.type = "button"
      item.className = "block w-full text-left px-3 py-2 text-gray-900 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
      item.textContent = opt.text
      item.dataset.value = opt.value
      item.addEventListener("click", (e) => {
        e.stopPropagation()
        this.toggle(opt.value, true)
        this.searchEl.value = ""
        this.searchEl.focus()
      })
      this.dropdownEl.appendChild(item)
    })
  }

  // -- Actions --

  filter() {
    this.openDropdown()
    this.renderDropdown(this.searchEl.value)
  }

  openDropdown() {
    this.dropdownEl.classList.remove("hidden")
  }

  closeDropdown() {
    this.dropdownEl.classList.add("hidden")
  }

  onKeydown(e) {
    if (e.key === "Escape") {
      this.closeDropdown()
      this.searchEl.blur()
    } else if (e.key === "Backspace" && this.searchEl.value === "") {
      const selected = this.selectedOptions()
      if (selected.length > 0) {
        this.toggle(selected[selected.length - 1].value, false)
      }
    }
  }

  onDocumentClick(e) {
    if (!this.element.contains(e.target)) this.closeDropdown()
  }

  toggle(value, select) {
    const opt = Array.from(this.selectTarget.options).find((o) => o.value === value)
    if (!opt) return
    opt.selected = select
    this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.renderChips()
    this.renderDropdown(this.searchEl.value)
  }

  // -- Internals --

  allOptions() {
    return Array.from(this.selectTarget.options).map((o) => ({
      value: o.value,
      text: o.text,
      selected: o.selected
    }))
  }

  selectedOptions() {
    return this.allOptions().filter((o) => o.selected)
  }
}
